import { css } from '@/features/shared/styles'
import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { getFeaturedPromoCardProperties } from '@/features/shared/analytics/promoCard'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { Button } from '@shipt/design-system-buttons'
import { type HeroCardData } from '@/features/cms/components/CMS/types'
import { getPromotionFromData } from '@/features/cms/components/CMS/utils/promotion'
import { Badge } from '@/features/cms/components/CMS/components/Shelves/Badge'
import { useSelectPromoStore } from '@/features/cms/components/CMS/hooks/useSelectPromoStore'
import { Body, Headline } from '@shipt/design-system-typography'
import { useCMSTrackingEvents } from '@/features/cms/components/CMS/CMSContext'
import { Column, Row } from '@shipt/design-system-layouts'
import { Avatar } from '@shipt/design-system-avatar'
import { TransparentAnchor, TransparentButton } from '@/features/shared/elements/Buttons'
import { theme } from '@/features/shared/constants/shiptTheme'
import { useDeriveColorProps } from '@/features/cms/components/CMS/hooks/useDeriveColorProps'

type Props = {
  heroCardData: HeroCardData
  index: number
  shelfIndex: number
  shelfName: string
}

/**
 * This component corresponds to the `hero_card` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/hero_card
 * - **Production** - https://cms.shipt.com/content-type/pool/hero_card
 */
export const HeroCardV2 = ({ heroCardData, index, shelfIndex, shelfName }: Props) => {
  const { trackCMSPromoCardClicked } = useCMSTrackingEvents()
  const {
    data: {
      heading,
      sub_heading,
      image: { src: imgUrl },
      link: { text: buttonText, url: destinationUrl },
      store_cards,
      badge,
      background_color,
      text_color,
    },
  } = heroCardData

  const promotion = getPromotionFromData(heroCardData)

  const derivedBackgroundColor = useDeriveColorProps(background_color ?? 'neutral-default')
  const surface = text_color ?? derivedBackgroundColor.surface

  const { selectPromoStore, isLoading, normalizedUrl, renderLinkInCard } = useSelectPromoStore(
    promotion,
    destinationUrl,
  )

  const handleOnClick = () => {
    if (isLoading) return

    trackCMSPromoCardClicked(
      getFeaturedPromoCardProperties({
        promotion,
        displayFormat: DISPLAY_FORMAT.HORIZONTAL,
        listIndex: index,
        shelfIndex,
        subLocation: shelfName,
        nextLocation: normalizedUrl,
      }),
    )

    selectPromoStore()
  }
  const { id } = promotion

  const cardContent = (
    <>
      <ContentSection
        $backgroundColor={derivedBackgroundColor.color}
        spacing="sm"
        flexGrow={1}
        align="start"
        padding={['lg', 'xs', 'lg', 'lg']}
        justify="space-between"
      >
        <Column spacing="xs">
          <StoreList>
            {store_cards?.slice(0, 3).map(({ data }) => {
              return (
                <CardAvatar
                  key={data.retailer_id}
                  type="image"
                  colorScheme="retailer"
                  hasBorder
                  retailerColor={data.background_color ?? 'white'}
                  image={<Image src={data.logo.src} alt={data.logo.alt} height={30} width={30} />}
                />
              )
            })}
          </StoreList>
          <Headline id={`promo_${id}_title`} variant="primary" surface={surface} size="sm" as="h3">
            {heading}
          </Headline>
          {!!sub_heading && (
            <Body size="md" lineHeight="compact" surface={surface}>
              {sub_heading}
            </Body>
          )}
        </Column>
        <Button size="sm" loading={isLoading} forwardedAs="span" surface={surface}>
          {buttonText}
        </Button>
      </ContentSection>
      <ImageContainer>
        <CardImage src={imgUrl} alt={heading} width={117} height={156} preload={index === 0} />
      </ImageContainer>
    </>
  )

  return (
    <>
      {!!renderLinkInCard && (
        <CardContainer
          href={normalizedUrl}
          onClick={(e) => {
            e.preventDefault()
            handleOnClick()
          }}
          aria-label={`${heading}, ${buttonText}`}
        >
          {!!badge && <Badge badge={badge} />}
          {cardContent}
        </CardContainer>
      )}
      {!renderLinkInCard && (
        <CardContainerButton onClick={handleOnClick} aria-label={`${heading}, ${buttonText}`}>
          {!!badge && <Badge badge={badge} />}
          {cardContent}
        </CardContainerButton>
      )}
    </>
  )
}

const CardAvatar = styled(Avatar)`
  border-width: 1px;
`

const cardContainerStyles = css`
  position: relative;
  height: 100%;
  border-radius: 0.5rem;
  border: 1px solid ${theme.color.border.base.neutral.subtle};
  text-wrap: balance;
  width: 100%;
  display: flex;
  cursor: pointer;
  text-align: left;
`

const CardContainer = styled(TransparentAnchor)`
  ${cardContainerStyles}
`

const CardContainerButton = styled(TransparentButton)`
  ${cardContainerStyles}
`

const ContentSection = styled(Column)<{ $backgroundColor: string }>`
  min-width: 0;
  flex-basis: 0%;
  flex-grow: 1;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: ${({ $backgroundColor }) => ($backgroundColor ? '0.5rem 0 0 0.5rem' : '0')};
`

const StoreList = styled(Row)`
  & > *:not(:first-child) {
    margin-left: -8px;
  }
`

const CardImage = styled(Image)`
  flex-grow: 1;
  aspect-ratio: 3 / 4;
  width: auto;
  object-fit: cover;
  object-position: center left;
  min-height: 0;
  flex-basis: 0;
  height: 100%;
`

const ImageContainer = styled(Column)`
  position: relative;
  border-radius: 0 0.5rem 0.5rem 0;
  overflow: hidden;
  flex-basis: auto;
`
